import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import externalExamGroupsStore from '../../../../../stores/external-exam-groups-store';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { ExternalExamEntityWithDetails } from '../../../../../api/types/external-exams/external-exam-group-crud';
import dateUtils from '../../../../../utils/date-format';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';

interface ExternalExamsRegistrationsSideBarFilterProps {
  onSearch: (groupId: number, examId: number, branchIds: number[]) => void;
}

export const ExternalExamsRegistrationsSideBarFilter: React.FC<ExternalExamsRegistrationsSideBarFilterProps> =
  observer(({ onSearch }) => {
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
    const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);

    useEffect(() => {
      externalExamGroupsStore.fetchGroups();
    }, []);

    const selectedGroup = externalExamGroupsStore.groups.find(
      (g) => g.id === selectedGroupId,
    );

    const handleGroupToggle = (groupId: number) => {
      if (selectedGroupId === groupId) {
        setSelectedGroupId(null);
        setSelectedExamId(null);
      } else {
        setSelectedGroupId(groupId);
        setSelectedExamId(null);
      }
    };

    const handleExamToggle = (examId: number) => {
      if (selectedExamId === examId) {
        setSelectedExamId(null);
      } else {
        setSelectedExamId(examId);
      }
    };

    const handleBranchToggle = (branchId: number) => {
      setSelectedBranchIds((prev) =>
        prev.includes(branchId)
          ? prev.filter((b) => b !== branchId)
          : [...prev, branchId],
      );
    };

    const handleSearch = () => {
      if (selectedGroupId && selectedExamId) {
        onSearch(selectedGroupId, selectedExamId, selectedBranchIds);
      }
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        marginBottom: '16px',
        color: '#333',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      item: {
        padding: '8px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        marginBottom: '4px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      selectedItem: {
        backgroundColor: '#e3f2fd',
        border: '1px solid #2196F3',
      },
      unselectedItem: {
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
      },
      itemLabel: {
        fontSize: '0.95rem',
        color: '#333',
      },
      examDetails: {
        fontSize: '0.85rem',
        color: '#666',
        marginTop: '4px',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>סינונים</div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranchIds}
            onItemToggle={handleBranchToggle}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <CustomExpander defaultExpanded={true}>
            <div>מקבצי בחינות</div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              {externalExamGroupsStore.groups.map((group) => (
                <div
                  key={group.id}
                  onClick={() => handleGroupToggle(group.id)}
                  style={{
                    ...styles.item,
                    ...(selectedGroupId === group.id
                      ? styles.selectedItem
                      : styles.unselectedItem),
                  }}
                >
                  <i
                    className={
                      selectedGroupId === group.id
                        ? 'pi pi-check-circle'
                        : 'pi pi-circle'
                    }
                    style={{
                      color:
                        selectedGroupId === group.id ? '#2196F3' : '#9e9e9e',
                    }}
                  />
                  <div>
                    <div style={styles.itemLabel}>{group.groupName}</div>
                    <div style={styles.examDetails}>שנה: {group.year}</div>
                  </div>
                </div>
              ))}
            </div>
          </CustomExpander>

          {selectedGroup && selectedGroup.exams.length > 0 && (
            <CustomExpander defaultExpanded={true}>
              <div>מועדים</div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {selectedGroup.exams.map(
                  (exam: ExternalExamEntityWithDetails) => (
                    <div
                      key={exam.id}
                      onClick={() => handleExamToggle(exam.id)}
                      style={{
                        ...styles.item,
                        ...(selectedExamId === exam.id
                          ? styles.selectedItem
                          : styles.unselectedItem),
                      }}
                    >
                      <i
                        className={
                          selectedExamId === exam.id
                            ? 'pi pi-check-circle'
                            : 'pi pi-circle'
                        }
                        style={{
                          color:
                            selectedExamId === exam.id ? '#2196F3' : '#9e9e9e',
                        }}
                      />
                      <div>
                        <div style={styles.itemLabel}>
                          {exam.abstractExternalExam.examName}
                        </div>
                        <div style={styles.examDetails}>
                          {dateUtils.displayFormatDate(exam.startAt)}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </CustomExpander>
          )}
        </div>

        <div
          style={{
            marginTop: '16px',
            padding: '8px',
            borderTop: '1px solid #e9ecef',
          }}
        >
          <Button
            label="אישור"
            icon="pi pi-check"
            style={{
              width: '100%',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onClick={handleSearch}
            disabled={!selectedGroupId || !selectedExamId}
          />
        </div>
      </div>
    );
  });
